import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Newspaper, ExternalLink, Clock, Zap, ChevronDown, ChevronUp } from 'lucide-react';

export interface Article {
  id?: string;
  title: string;
  url: string;
  source: string;
  imageUrl: string;
  publishedAt: number;
  summary: string;
}

export default function EvNews() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const q = query(collection(db, 'news'), orderBy('publishedAt', 'desc'), limit(20));
        const snapshot = await getDocs(q);
        
        const fetchedArticles = snapshot.docs.map(doc => {
          const data = doc.data();
          
          // 🆕 THE TIME MACHINE FIX: Convert Firestore Timestamps to standard Milliseconds
          let timeMs = data.publishedAt;
          
          // Check if it's a special Firestore Timestamp object
          if (data.publishedAt && typeof data.publishedAt.toMillis === 'function') {
            timeMs = data.publishedAt.toMillis();
          } 
          // Backup check just in case it comes through as a raw object
          else if (data.publishedAt && data.publishedAt.seconds) {
            timeMs = data.publishedAt.seconds * 1000;
          }

          return {
            id: doc.id,
            ...data,
            publishedAt: timeMs // Save the corrected millisecond time
          };
        }) as Article[];

        setArticles(fetchedArticles);
      } catch (error) {
        console.error("Error fetching news:", error);
      }
      setLoading(false);
    };

    fetchNews();
  }, []);

  const timeAgo = (timestamp: number) => {
    if (!timestamp) return 'Recently';
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " years ago";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " months ago";
    interval = seconds / 86400;
    if (interval >= 1) return Math.floor(interval) + (Math.floor(interval) === 1 ? " day ago" : " days ago");
    interval = seconds / 3600;
    if (interval >= 1) return Math.floor(interval) + (Math.floor(interval) === 1 ? " hour ago" : " hours ago");
    interval = seconds / 60;
    if (interval >= 1) return Math.floor(interval) + " minutes ago";
    return "Just now";
  };

  // 🆕 THE TRANSLATOR: Converts raw AI Markdown into beautiful HTML
  const formatSummary = (text: string) => {
    if (!text) return null;
    
    // 1. Split the text everywhere the AI hit "Enter"
    return text.split('\n').map((paragraph, index) => {
      // Ignore completely empty lines
      if (!paragraph.trim()) return null;

      // 2. Look for the **bold** markers
      const parts = paragraph.split(/(\*\*.*?\*\*)/g);

      return (
        <p key={index} className="mb-3 last:mb-0 leading-relaxed">
          {parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              // Strip the asterisks and wrap in a bold tag with a darker text color
              return <strong key={i} className="font-bold text-slate-800">{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
        </p>
      );
    });
  };

  return (
    <div className="w-full max-w-7xl mx-auto animate-fadeIn">
      <div className="flex items-center gap-3 mb-8 pb-4 border-b border-gray-200">
        <div className="bg-blue-100 p-3 rounded-xl">
          <Newspaper className="w-8 h-8 text-blue-600" />
        </div>
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Live EV News</h1>
          <p className="text-slate-500 font-medium mt-1">AI-curated updates on Tesla, Rivian, Lucid, and the EV market.</p>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl h-80 border border-gray-200 animate-pulse flex flex-col">
              <div className="h-48 bg-gray-200 rounded-t-2xl"></div>
              <div className="p-5 flex-1 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-6 bg-gray-200 rounded w-full"></div>
                <div className="h-6 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          ))}
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-300">
          <Zap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-gray-700">No News Yet</h3>
          <p className="text-gray-500 mt-1">The news bot is warming up its engines.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {articles.map((article) => {
            const isExpanded = expandedId === article.id;

            return (
              <div 
                key={article.id} 
                onClick={() => setExpandedId(isExpanded ? null : article.id!)}
                className={`bg-white rounded-2xl border ${isExpanded ? 'border-blue-400 shadow-xl ring-4 ring-blue-50' : 'border-gray-200 hover:shadow-xl hover:-translate-y-1'} overflow-hidden transition-all duration-300 group flex flex-col cursor-pointer`}
              >
                <div className="relative h-48 overflow-hidden bg-slate-100 flex-shrink-0">
                  <img 
                    src={article.imageUrl || "https://placehold.co/600x400/slate/white?text=EV+News"} 
                    alt={article.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur text-slate-800 text-xs font-black px-3 py-1 rounded-full uppercase tracking-wide shadow-sm">
                    {article.source}
                  </div>
                </div>
                
                <div className="p-5 flex flex-col flex-1">
                  <h3 className={`text-lg font-bold leading-tight mb-3 transition-colors ${isExpanded ? 'text-blue-600' : 'text-slate-900 group-hover:text-blue-600'}`}>
                    {article.title}
                  </h3>
                  
                  {/* 🆕 Swapped out the raw text for our new formatSummary function */}
                  <div className={`text-slate-600 text-sm mb-4 ${isExpanded ? '' : 'line-clamp-4'}`}>
                    {formatSummary(article.summary)}
                  </div>
                  
                  {isExpanded && (
                    <div className="mb-4 mt-2">
                      <a 
                        href={article.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()} 
                        className="flex items-center justify-center gap-2 w-full bg-slate-900 hover:bg-blue-600 text-white py-3 rounded-xl font-bold transition-colors shadow-md"
                      >
                        Read Original Article <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                    <div className="flex items-center text-xs text-slate-500 font-medium gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      {timeAgo(article.publishedAt)}
                    </div>
                    <div className={`text-blue-600 flex items-center text-sm font-bold gap-1 transition-transform ${!isExpanded && 'group-hover:translate-x-1'}`}>
                      {isExpanded ? (
                        <>Close <ChevronUp className="w-4 h-4" /></>
                      ) : (
                        <>Read Summary <ChevronDown className="w-4 h-4" /></>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}